/* eslint-disable react-hooks/rules-of-hooks */
import { LRUCache } from "lru-cache";
import { action, makeAutoObservable, runInAction } from "mobx";
import { Channel, Message, Nullable } from "revolt.js";

import { calcMissingReadMarks } from "../calcMissingReadMarks";
import { TIME } from "../time";
import { ScrollState } from "./types";

export type RendererParams = {
    pinned?: boolean;
};

export const SMOOTH_SCROLL_ON_RECEIVE = false;

export const MESSAGE_LIMIT = 1000;

export class ChannelRenderer {
    channel: Channel;

    state: "LOADING" | "WAITING_FOR_NETWORK" | "EMPTY" | "RENDER" = "LOADING";
    scrollState: ScrollState = { type: "ScrollToBottom" };
    atTop: Nullable<boolean> = null;
    atBottom: Nullable<boolean> = null;
    messages: Message[] = [];

    stale = false;
    fetching = false;
    initInFlight = false;
    scrollPosition = 0;
    scrollAnchored = false;

    private _params?: RendererParams;

    get isPinned() {
        return this._params?.pinned;
    }

    constructor(channel: Channel, params?: RendererParams) {
        this.channel = channel;
        this._params = params;

        makeAutoObservable(this, {
            channel: false,
            scrollPosition: false,
            scrollAnchored: false,
            init: action,
            emitScroll: action,
            markStale: action,
            complete: action,
        });

        this.receive = this.receive.bind(this);
        this.updated = this.updated.bind(this);
        this.delete = this.delete.bind(this);
        this.onMessageRead = this.onMessageRead.bind(this);

        const client = this.channel.client;
        client.addListener("message", this.receive);
        client.addListener("message/updated", this.updated);
        client.addListener("message/delete", this.delete);
        client.addListener("message/read", this.onMessageRead);
    }

    destroy() {
        const client = this.channel.client;
        client.removeListener("message", this.receive);
        client.removeListener("message/updated", this.updated);
        client.removeListener("message/delete", this.delete);
        client.removeListener("message/read", this.onMessageRead);
    }

    private receive(message: Message) {
        if (message.channel_id !== this.channel._id) return;
        if (this.state !== "RENDER") return;
        if (this.messages.find((x) => x._id === message._id)) return;
        if (!this.atBottom) return;

        let messages = [...this.messages, message];
        let atTop = this.atTop;
        if (messages.length > MESSAGE_LIMIT) {
            console.log("reach message limit");
            messages = messages.slice(messages.length - MESSAGE_LIMIT);
            atTop = false;
        }

        runInAction(() => {
            this.messages = messages;
            this.atTop = atTop;

            this.emitScroll({
                type: "StayAtBottom",
                smooth: SMOOTH_SCROLL_ON_RECEIVE,
            });
        });
    }

    private updated(message: Message, _data: any) {
        if (this.isPinned) {
            /**
             * Весь вот этот код нужен чтобы найти позицию сообщения для прикрепленных сообщений и
             * он нужен ТОЛЬКО для прикрепленных сообщений
             */
            const found = this.messages.find((msg) => msg._id === message._id);
            if (message.pinned && !found) {
                this.channel
                    .fetchMessagesWithUsers({
                        pinned: true,
                        before: message._id,
                        limit: 1,
                    })
                    .then((messagesBefore) => {
                        const [msgBefore] = messagesBefore.messages;
                        if (!msgBefore) {
                            this.messages = [message, ...this.messages];
                            return;
                        }

                        const messageBeforeIndex = this.messages.findIndex(
                            (msg) => msg._id === msgBefore._id,
                        );
                        if (messageBeforeIndex === -1) {
                            return;
                        }

                        const newMessages = [...this.messages];
                        newMessages.splice(messageBeforeIndex + 1, 0, message);
                        this.messages = newMessages;
                    });
            } else if (!message.pinned && found) {
                this.messages = this.messages.filter(
                    (msg) => msg._id !== message._id,
                );
            }
        }

        this.emitScroll({
            type: "StayAtBottom",
            smooth: false,
        });
    }

    private delete(id: string) {
        const channel = this.channel;
        if (!channel) return;
        if (this.state !== "RENDER") return;

        const index = this.messages.findIndex((x) => x._id === id);

        if (index > -1) {
            runInAction(() => {
                this.messages.splice(index, 1);
                this.emitScroll({ type: "StayAtBottom" });
            });
        }
    }

    private onMessageRead(packet: any) {
        if (packet.channel_id !== this.channel._id) return;
        const msg = this.channel.client.messages.get(packet.message_id);
        if (msg && this.state === "RENDER" && msg.readMarks.length > 0) {
            calcMissingReadMarks(
                this.channel.client,
                packet.channel_id,
                new Set(this.messages.map((m) => m._id)),
            );
        }
    }

    async init(message_id?: string, last_id?: string) {
        if (message_id) {
            if (this.state === "RENDER") {
                const message = this.messages.find((x) => x._id === message_id);

                if (message) {
                    this.emitScroll({
                        type: "ScrollToView",
                        id: message_id,
                    });

                    return;
                }
            }
        }

        if (!message_id && this.state === "LOADING" && this.initInFlight) {
            return;
        }

        // Nearby init (message_id) also marks the fetch in flight; the guard
        // above stays message_id-only, so nearby clicks behave as before.
        this.initInFlight = true;

        this.stale = false;
        this.state = "LOADING";
        this.doInit(message_id, undefined, last_id);
    }

    emitScroll(state: ScrollState) {
        this.scrollState = state;
    }

    markStale() {
        this.stale = true;
    }

    complete() {
        this.fetching = false;
    }

    async reloadStale() {
        if (this.stale) {
            this.stale = false;
            await this.init();
        }
    }

    async loadTop(ref?: HTMLDivElement) {
        if (this.fetching) return;
        this.fetching = true;
        const messageContainer = ref?.children[0];

        const generateScroll = (end: string): ScrollState => {
            if (ref) {
                let heightRemoved = this.atTop ? -200 : 52,
                    removing = false;
                if (messageContainer) {
                    for (const child of Array.from(messageContainer.children)) {
                        if (
                            removing ||
                            (child.id?.length === 26 &&
                                child.id.localeCompare(end) === 1)
                        ) {
                            removing = true;
                            const marginPart = parseInt(
                                window
                                    .getComputedStyle(child)
                                    .marginTop.slice(0, -2),
                                10,
                            );
                            const computedHeight =
                                (child as HTMLElement).offsetHeight +
                                marginPart;
                            heightRemoved += computedHeight;
                        }
                    }
                }

                console.log(
                    "height removed",
                    heightRemoved,
                    "removing",
                    removing,
                );
                return {
                    type: "OffsetTop",
                    previousHeight: ref.scrollHeight - heightRemoved,
                };
            }
            return {
                type: "OffsetTop",
                previousHeight: 0,
            };
        };

        if (await this.doLoadTop(generateScroll)) {
            this.fetching = false;
        }
    }

    async loadBottom(ref?: HTMLDivElement) {
        if (this.fetching) return;
        this.fetching = true;

        function generateScroll(start: string): ScrollState {
            if (ref) {
                let heightRemoved = 0,
                    removing = true;
                const messageContainer = ref.children[0];
                if (messageContainer) {
                    for (const child of Array.from(messageContainer.children)) {
                        // If this child has a ulid check whether it was removed.
                        if (
                            removing /* ||
                            (child.id?.length === 26 &&
                                child.id.localeCompare(start) === -1)*/
                        ) {
                            heightRemoved +=
                                child.clientHeight +
                                // We also need to take into account the top margin of the container.
                                parseInt(
                                    window
                                        .getComputedStyle(child)
                                        .marginTop.slice(0, -2),
                                    10,
                                );
                        }

                        if (
                            child.id?.length === 26 &&
                            child.id.localeCompare(start) !== -1
                        )
                            removing = false;
                    }
                }

                return {
                    type: "ScrollTop",
                    y: ref.scrollTop - heightRemoved,
                };
            }
            return {
                type: "ScrollToBottom",
            };
        }

        if (await this.doLoadBottom(generateScroll)) {
            this.fetching = false;
        }
    }

    async jumpToBottom(smooth: boolean) {
        if (this.state === "RENDER" && this.atBottom) {
            this.emitScroll({ type: "ScrollToBottom", smooth });
        } else {
            await this.doInit(undefined, true);
        }
    }

    private async doInit(nearby?: string, smooth?: boolean, last_id?: string) {
        if (this.channel.client.websocket.connected) {
            if (nearby) {
                this.channel
                    .fetchMessagesWithUsers({
                        nearby,
                        limit: 100,
                        pinned: this.isPinned,
                    })
                    .then(({ messages }) => {
                        messages.sort((a, b) => a._id.localeCompare(b._id));

                        runInAction(() => {
                            this.state = "RENDER";
                            this.initInFlight = false;
                            this.messages = messages;
                            this.atTop = messages.length < 100;
                            this.atBottom = messages.length < 100;

                            this.emitScroll({
                                type: "ScrollToView",
                                id: nearby,
                            });
                        });
                        return messages;
                    })
                    .catch((e) => {
                        runInAction(() => {
                            this.state = "EMPTY";
                            this.initInFlight = false;
                        });
                        throw e;
                    })
                    .then((messages) => {
                        const messageIds = messages.map((x) => x._id);
                        return this.channel
                            .fetchReadsVec(messageIds)
                            .finally(() => {
                                calcMissingReadMarks(
                                    this.channel.client,
                                    this.channel._id,
                                    new Set(messageIds),
                                );
                            });
                    });
            } else {
                this.channel
                    .fetchMessagesWithUsers({ pinned: this.isPinned })
                    .then(({ messages }) => {
                        messages.reverse();

                        runInAction(() => {
                            this.state = "RENDER";
                            this.initInFlight = false;
                            this.messages = messages;
                            this.atTop = messages.length < 50;
                            this.atBottom = true;

                            if (last_id) {
                                const message = messages.find(
                                    (x) => x._id === last_id,
                                );
                                if (message) {
                                    this.emitScroll({
                                        type: "ScrollToView",
                                        id: last_id,
                                    });
                                } else {
                                    this.emitScroll({
                                        type: "ScrollToBottom",
                                        smooth,
                                    });
                                }
                            } else {
                                this.emitScroll({
                                    type: "ScrollToBottom",
                                    smooth,
                                });
                            }
                        });
                        return messages;
                    })
                    .catch((e) => {
                        runInAction(() => {
                            this.state = "EMPTY";
                            this.initInFlight = false;
                        });
                        throw e;
                    })
                    .then((messages) => {
                        const messageIds = messages.map((x) => x._id);
                        return this.channel
                            .fetchReadsVec(messageIds)
                            .finally(() => {
                                calcMissingReadMarks(
                                    this.channel.client,
                                    this.channel._id,
                                    new Set(messageIds),
                                );
                            });
                    });
            }
        } else {
            runInAction(() => {
                this.state = "WAITING_FOR_NETWORK";
                this.initInFlight = false;
            });
        }
    }

    private async doLoadTop(generateScroll: (start: string) => ScrollState) {
        const channel = this.channel;
        if (!channel) return true;

        if (this.state !== "RENDER") return true;
        if (this.atTop) return true;

        const { messages: data } = await this.channel.fetchMessagesWithUsers({
            pinned: this.isPinned,
            before: this.messages[0]._id,
        });

        runInAction(() => {
            if (data.length === 0) {
                this.atTop = true;
                return;
            }

            data.reverse();
            this.messages = [...data, ...this.messages];

            if (data.length < 50) {
                this.atTop = true;
            }

            if (this.messages.length > MESSAGE_LIMIT) {
                this.messages = this.messages.slice(0, MESSAGE_LIMIT);
                this.atBottom = false;
            }

            const lastMessage = this.messages[this.messages.length - 1];
            this.emitScroll(generateScroll(lastMessage._id));
        });

        // Fetch read marks for all currently loaded messages before back-propagation.
        const allIds = this.messages.map((m) => m._id);
        channel.fetchReadsVec(allIds).then(() => {
            calcMissingReadMarks(channel.client, channel._id, new Set(allIds));
        });
    }

    private async doLoadBottom(generateScroll: (start: string) => ScrollState) {
        const channel = this.channel;
        if (!channel) return true;

        if (this.state !== "RENDER") return true;
        if (this.atBottom) return true;

        const { messages: data } = await this.channel.fetchMessagesWithUsers({
            pinned: this.isPinned,
            after: this.messages[this.messages.length - 1]._id,
            sort: "Oldest",
        });

        runInAction(() => {
            if (data.length === 0) {
                this.atBottom = true;
                return;
            }

            this.messages.splice(this.messages.length, 0, ...data);

            if (data.length < 50) {
                this.atBottom = true;
            }

            if (this.messages.length > MESSAGE_LIMIT) {
                this.messages.splice(0, this.messages.length - MESSAGE_LIMIT);
                this.atTop = false;
            }

            this.emitScroll(generateScroll(this.messages[0]._id));
        });

        // Fetch read marks for all currently loaded messages before back-propagation.
        const allIds = this.messages.map((m) => m._id);
        channel.fetchReadsVec(allIds).then(() => {
            calcMissingReadMarks(channel.client, channel._id, new Set(allIds));
        });
    }
}

class RendererCache {
    private _cache: any;

    constructor(cache: any) {
        this._cache = cache;
    }

    get(channel: Channel, params?: RendererParams) {
        let renderer = this._cache.get(channel._id);
        if (!renderer) {
            renderer = new ChannelRenderer(channel, params);
            this._cache.set(channel._id, renderer);
        }
        return renderer;
    }

    remove(channelId: string) {
        this._cache.delete(channelId);
    }

    get size() {
        return this._cache.size;
    }
}

const opts = {
    max: 10,
    ttl: TIME.MINUTE * 5,
};

const renderers = new RendererCache(
    new LRUCache<string, ChannelRenderer>(opts),
);

export function getRenderer(channel: Channel) {
    return renderers.get(channel);
}

export function deleteRenderer(channel_id: string) {
    renderers.remove(channel_id);
}

const pinnedRenderers = new RendererCache(
    new LRUCache<string, ChannelRenderer>(opts),
);

export function getPinnedRenderer(channel: Channel) {
    return pinnedRenderers.get(channel, { pinned: true });
}

export function deletePinnedRenderer(channel: Channel) {
    pinnedRenderers.remove(channel._id);
}
